import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { WifiOff, RefreshCw, Check, CloudOff, Cloud } from 'lucide-react';
import { Button } from './ui/button';
import { useOfflineSync } from '../lib/offlineSync';
import { useLanguage } from '../context/LanguageContext';

export default function OfflineIndicator() {
  const { isOnline, pendingCount, isSyncing, triggerSync } = useOfflineSync();
  const { language } = useLanguage();

  // Don't show anything if online and no pending transactions
  if (isOnline && pendingCount === 0) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="fixed top-0 left-0 right-0 z-50 safe-area-top"
      >
        <div className={`px-4 py-2 flex items-center justify-between text-sm ${
          isOnline 
            ? 'bg-amber-500 text-white' 
            : 'bg-red-500 text-white'
        }`}>
          <div className="flex items-center gap-2">
            {!isOnline ? (
              <>
                <WifiOff className="w-4 h-4" />
                <span>
                  {language === 'fr' ? 'Mode hors ligne' : language === 'pt' ? 'Modo offline' : 'Offline mode'}
                </span>
              </>
            ) : (
              <>
                <CloudOff className="w-4 h-4" />
                <span>
                  {pendingCount} {language === 'fr' 
                    ? `transaction${pendingCount > 1 ? 's' : ''} en attente` 
                    : language === 'pt'
                    ? `transação${pendingCount > 1 ? 'ões' : ''} pendente${pendingCount > 1 ? 's' : ''}`
                    : `pending transaction${pendingCount > 1 ? 's' : ''}`}
                </span>
              </>
            )}
          </div>
          
          {isOnline && pendingCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={triggerSync}
              disabled={isSyncing}
              className="text-white hover:bg-white/20 h-7 px-2"
            >
              {isSyncing ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Cloud className="w-4 h-4 mr-1" />
                  {language === 'fr' ? 'Sync' : language === 'pt' ? 'Sincronizar' : 'Sync'}
                </>
              )}
            </Button>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

// Compact indicator for header/navbar
export function OfflineBadge() {
  const { isOnline, pendingCount } = useOfflineSync();
  
  if (isOnline && pendingCount === 0) return null;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
      isOnline 
        ? 'bg-amber-100 text-amber-700'
        : 'bg-red-100 text-red-700'
    }`}>
      {!isOnline ? (
        <WifiOff className="w-3 h-3" />
      ) : (
        <>{pendingCount}</>
      )}
    </span>
  );
}
